import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { GoodsReturnNoteLine, GoodsReturnNoteRow } from '@/modules/inventory/goods-return-notes/types'
import { lineTaxAmount, lineTaxableAmount, lineTotal } from '@/modules/inventory/goods-return-notes/types'

const toArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if (Array.isArray(p.data)) return p.data
    if (Array.isArray(p.result)) return p.result
  }
  return []
}

const unwrap = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data
  }
  return payload
}

/** GRN row from `GET /api/GoodsReturns/GetGrns/{branchId}` (goods return context). */
export type GoodsReturnGrnRow = {
  id: number
  code: string
  reference: string
  purchaseOrderId: number
  date: string
  vendorId: number
  warehouseId: number
  taxableAmount?: number
  taxAmount?: number
  discount?: number
  totalAmount?: number
  payableBalance?: number
  isReturnCreated?: boolean
  isBillCreated?: boolean
  branchId?: number
}

export type GoodsReturnVendorOption = {
  vendorId: number
  vendorNameEn: string
  vendorNameAr: string
}

export type GoodsReturnWarehouseOption = {
  id: number
  code: string
  name: string
}

export type GoodsReturnTaxRateOption = {
  id: number
  taxRateNameEn: string
  taxRateNameAr: string
  percent: number
  isDefault: boolean
}

/** One line for `itemList[n].*` and `goodsReturnLinesJson` — `/api/GoodsReturns/Save` (Swagger). */
export type GoodsReturnSaveLine = {
  id: number
  returnId: number
  itemId: number
  batchId: number
  quantity: number
  unitPrice: number
  discount: number
  discountAmount: number
  tax: number
  taxPercent: number
  taxableAmount: number
  taxAmount: number
  lineAmount: number
  isDeleted: string
}

export type GoodsReturnSavePayload = {
  id: number
  code: string
  purchaseorderCode: string
  grnCode: string
  reference: string
  vendorName: string
  warehouseName: string
  grnId: number
  orderId: number
  date: string
  vendorId: number
  warehouseId: number
  attachmentPath: string
  taxableAmount: number
  taxAmount: number
  discount: number
  totalAmount: number
  isBillGenerated: boolean
  isReturnCreated: boolean
  status: number
  branchId: number
  createdDate: string
  createdBy: number
  modifiedDate: string
  modifiedBy: number
  attachmentFile?: File | null
  itemList: GoodsReturnSaveLine[]
}

export function mapFormLineToGoodsReturnSaveLine(
  l: GoodsReturnNoteLine,
  returnId: number,
): GoodsReturnSaveLine {
  const qty = Math.max(0, Number(l.quantityReturned) || 0)
  const price = Math.max(0, Number(l.unitPrice) || 0)
  const discPct = 0
  const discAmt = 0
  const taxId = Number(l.taxRateId) || 0
  // Save (returnId === 0): line id must be 0 — do not send GRN line id. Update: goods return line PK.
  const lineIdForApi =
    returnId > 0 ? Number(l.goodsReturnLineId) || 0 : 0
  return {
    id: lineIdForApi,
    returnId,
    itemId: Number(l.itemId) || 0,
    batchId: Number(l.batchId) || 0,
    quantity: qty,
    unitPrice: price,
    discount: discPct,
    discountAmount: discAmt,
    tax: taxId,
    taxPercent: Number(l.taxPercent) || 0,
    taxableAmount: Math.round(lineTaxableAmount(l) * 10000) / 10000,
    taxAmount: Math.round(lineTaxAmount(l) * 10000) / 10000,
    lineAmount: Math.round(lineTotal(l) * 10000) / 10000,
    isDeleted: 'false',
  }
}

function num(raw: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v)
  }
  return 0
}

function str(raw: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

function mapGrnRow(row: Record<string, unknown>): GoodsReturnGrnRow {
  const dateRaw = str(row, ['date', 'Date'])
  return {
    id: num(row, ['id', 'Id']),
    code: str(row, ['code', 'Code']),
    reference: str(row, ['reference', 'Reference']),
    purchaseOrderId: num(row, ['purchaseOrderId', 'PurchaseOrderId']),
    date: dateRaw ? dateRaw.slice(0, 10) : '',
    vendorId: num(row, ['vendorId', 'VendorId']),
    warehouseId: num(row, ['warehouseId', 'WarehouseId']),
    taxableAmount: num(row, ['taxableAmount', 'TaxableAmount']) || undefined,
    taxAmount: num(row, ['taxAmount', 'TaxAmount']) || undefined,
    discount: num(row, ['discount', 'Discount']) || undefined,
    totalAmount: num(row, ['totalAmount', 'TotalAmount']) || undefined,
    payableBalance: num(row, ['payableBalance', 'PayableBalance']) || undefined,
    isReturnCreated: row.isReturnCreated === true || row.IsReturnCreated === true,
    isBillCreated: row.isBillCreated === true || row.IsBillCreated === true,
    branchId: num(row, ['branchId', 'BranchId']) || undefined,
  }
}

export class GoodsReturnService {
  /**
   * `GET /api/GoodsReturns/GetAllGoodsReturns` — paged list; body shape:
   * `{ totalRecords, pageNumber, pageSize, data: [...] }`.
   */
  async getAllGoodsReturns(
    branchId: number,
    pageSize: number,
    pageNumber: number,
  ): Promise<{ items: GoodsReturnNoteRow[]; totalCount: number }> {
    const response = await apiClient.get(
      endpoints.goodsReturns.getAllGoodsReturns(branchId, pageSize, pageNumber),
    )
    const payload = response.data
    const top =
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? (payload as Record<string, unknown>)
        : null

    let list: unknown[] = []
    let totalCount = 0

    if (top) {
      totalCount = Number(top.totalRecords ?? top.totalCount ?? 0)
      const arr = top.data ?? top.Data ?? top.items ?? top.Items
      if (Array.isArray(arr)) list = arr
    }

    if (list.length === 0) {
      const raw = unwrap(payload)
      if (Array.isArray(raw)) {
        list = raw
      } else if (raw && typeof raw === 'object') {
        const r = raw as Record<string, unknown>
        if (Array.isArray(r.data)) list = r.data
        totalCount = Number(r.totalRecords ?? r.totalCount ?? totalCount)
      }
    }

    const items: GoodsReturnNoteRow[] = list.map((row) => {
      const x = row as Record<string, unknown>
      const orderCode =
        str(x, [
          'purchaseorderCode',
          'PurchaseorderCode',
          'purchaseOrderCode',
          'PurchaseOrderCode',
          'orderCode',
          'OrderCode',
        ]) || ''
      const dateRaw = str(x, ['date', 'Date'])
      return {
        id: num(x, ['id', 'Id']),
        code: str(x, ['code', 'Code']),
        reference: str(x, ['reference', 'Reference']),
        warehouse: str(x, ['warehouseName', 'WarehouseName', 'warehouse', 'Warehouse']),
        vendor: str(x, ['vendorName', 'VendorName', 'vendor', 'Vendor']),
        grnCode: str(x, ['grnCode', 'GrnCode']),
        orderCode,
        date: dateRaw ? dateRaw.slice(0, 10) : '',
        total: num(x, ['totalAmount', 'TotalAmount', 'total', 'Total']),
        isRefundCreated:
          x.isRefundCreated === true ||
          x.IsRefundCreated === true ||
          x.isRefundCreated === 1 ||
          x.IsRefundCreated === 1,
      }
    })

    const tc = Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : items.length
    return { items, totalCount: tc }
  }

  async getGrns(branchId: number): Promise<GoodsReturnGrnRow[]> {
    const response = await apiClient.get(endpoints.goodsReturns.getGrns(branchId))
    const arr = toArray(unwrap(response.data))
    return arr.map((x) => {
      const row = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>
      return mapGrnRow(row)
    })
  }

  /**
   * `GET /api/GoodsReturns/GetGrnLines/{id}` — `{ grn, lines[] }` for goods return form.
   */
  async getGrnLinesForReturn(grnId: number): Promise<{
    grn: GoodsReturnGrnRow | null
    lines: Record<string, unknown>[]
  }> {
    const response = await apiClient.get(endpoints.goodsReturns.getGrnLines(grnId))
    const root = unwrap(response.data)
    if (!root || typeof root !== 'object') {
      return { grn: null, lines: [] }
    }
    const obj = root as Record<string, unknown>
    const grnPayload = obj.grn ?? obj.Grn
    const linesPayload = obj.lines ?? obj.Lines

    const grn =
      grnPayload && typeof grnPayload === 'object'
        ? mapGrnRow(grnPayload as Record<string, unknown>)
        : null

    let lines: unknown[] = []
    if (Array.isArray(linesPayload)) lines = linesPayload
    else lines = toArray(linesPayload)

    return {
      grn,
      lines: lines.map((x) => (x && typeof x === 'object' ? (x as Record<string, unknown>) : {})),
    }
  }

  /** Master header for edit — `GET /api/GoodsReturns/GetGoodsReturnById/{id}`. */
  async getGoodsReturnById(id: number): Promise<Record<string, unknown>> {
    const response = await apiClient.get(endpoints.goodsReturns.getGoodsReturnById(id))
    const raw = unwrap(response.data)
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
    return raw as Record<string, unknown>
  }

  /** Lines for an existing return — `GET /api/GoodsReturns/GetGoodsReturnLinesByReturnId/{id}`. */
  async getGoodsReturnLinesByReturnId(returnId: number): Promise<Record<string, unknown>[]> {
    const response = await apiClient.get(endpoints.goodsReturns.getGoodsReturnLinesByReturnId(returnId))
    const raw = unwrap(response.data)
    if (Array.isArray(raw)) {
      return raw.map((x) => (x && typeof x === 'object' ? (x as Record<string, unknown>) : {}))
    }
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>
      const list = o.data ?? o.Data ?? o.lines ?? o.Lines
      if (Array.isArray(list)) {
        return list.map((x) => (x && typeof x === 'object' ? (x as Record<string, unknown>) : {}))
      }
    }
    return []
  }

  async getAllVendors(branchId: number): Promise<GoodsReturnVendorOption[]> {
    const response = await apiClient.get(endpoints.goodsReturns.getAllVendors(branchId))
    const arr = toArray(unwrap(response.data))
    return arr.map((x) => {
      const row = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>
      return {
        vendorId: num(row, ['vendorId', 'VendorId', 'id', 'Id']),
        vendorNameEn: str(row, ['vendorNameEn', 'VendorNameEn', 'vendorName', 'VendorName']),
        vendorNameAr: str(row, ['vendorNameAr', 'VendorNameAr']),
      }
    })
  }

  async getAllWarehouses(branchId: number): Promise<GoodsReturnWarehouseOption[]> {
    const response = await apiClient.get(endpoints.goodsReturns.getAllWarehouses(branchId))
    const arr = toArray(unwrap(response.data))
    return arr.map((x) => {
      const row = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>
      return {
        id: num(row, ['id', 'Id', 'warehouseId', 'WarehouseId']),
        code: str(row, ['code', 'Code']),
        name: str(row, ['name', 'Name', 'warehouseName', 'WarehouseName']),
      }
    })
  }

  async getAllTaxRates(): Promise<GoodsReturnTaxRateOption[]> {
    const response = await apiClient.get(endpoints.goodsReturns.getAllTaxRates)
    const arr = toArray(unwrap(response.data))
    return arr.map((x) => {
      const row = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>
      return {
        id: num(row, ['taxRateId', 'TaxRateId', 'id', 'Id']),
        taxRateNameEn: str(row, ['taxRateNameEn', 'TaxRateNameEn']),
        taxRateNameAr: str(row, ['taxRateNameAr', 'TaxRateNameAr']),
        percent: num(row, ['taxPercent', 'TaxPercent']),
        isDefault: row.isDefault === true || row.IsDefault === true,
      }
    })
  }

  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false')
      return
    }
    formData.append(key, String(value))
  }

  /** Same property order as Swagger `itemList` sample + `goodsReturnLinesJson`. */
  private normalizeLineForJson(l: GoodsReturnSaveLine): Record<string, number | string> {
    return {
      discount: l.discount,
      taxAmount: l.taxAmount,
      unitPrice: l.unitPrice,
      quantity: l.quantity,
      returnId: l.returnId,
      tax: l.tax,
      lineAmount: l.lineAmount,
      itemId: l.itemId,
      isDeleted: l.isDeleted,
      batchId: l.batchId,
      discountAmount: l.discountAmount,
      taxPercent: l.taxPercent,
      id: l.id,
      taxableAmount: l.taxableAmount,
    }
  }

  /** Swagger `goodsReturnLinesJson` is a **stringified JSON array** `[{...}]`, not `{ "returns": [...] }`. */
  private buildGoodsReturnLinesJson(lines: GoodsReturnSaveLine[]): string {
    const normalized = lines.map((l) => this.normalizeLineForJson(l))
    return JSON.stringify(normalized)
  }

  private buildSaveFormData(payload: GoodsReturnSavePayload, goodsReturnLinesJson: string): FormData {
    const formData = new FormData()
    const p = payload

    this.appendFormField(formData, 'Id', p.id)
    this.appendFormField(formData, 'Code', p.code)
    this.appendFormField(formData, 'purchaseorderCode', p.purchaseorderCode)
    this.appendFormField(formData, 'GrnCode', p.grnCode)
    this.appendFormField(formData, 'Reference', p.reference)
    this.appendFormField(formData, 'VendorName', p.vendorName)
    this.appendFormField(formData, 'WarehouseName', p.warehouseName)
    this.appendFormField(formData, 'GrnId', p.grnId)
    this.appendFormField(formData, 'OrderId', p.orderId)
    this.appendFormField(formData, 'Date', p.date)
    this.appendFormField(formData, 'VendorId', p.vendorId)
    this.appendFormField(formData, 'WarehouseId', p.warehouseId)
    this.appendFormField(formData, 'AttachmentPath', p.attachmentPath)
    this.appendFormField(formData, 'TaxableAmount', p.taxableAmount)
    this.appendFormField(formData, 'TaxAmount', p.taxAmount)
    this.appendFormField(formData, 'Discount', p.discount)
    this.appendFormField(formData, 'TotalAmount', p.totalAmount)
    this.appendFormField(formData, 'IsBillGenerated', p.isBillGenerated)
    this.appendFormField(formData, 'IsReturnCreated', p.isReturnCreated)
    this.appendFormField(formData, 'Status', p.status)
    this.appendFormField(formData, 'BranchId', p.branchId)
    this.appendFormField(formData, 'CreatedDate', p.createdDate)
    this.appendFormField(formData, 'CreatedBy', p.createdBy)
    this.appendFormField(formData, 'ModifiedDate', p.modifiedDate)
    this.appendFormField(formData, 'ModifiedBy', p.modifiedBy)

    p.itemList.forEach((line, index) => {
      const n = this.normalizeLineForJson(line)
      this.appendFormField(formData, `itemList[${index}].discount`, n.discount)
      this.appendFormField(formData, `itemList[${index}].taxAmount`, n.taxAmount)
      this.appendFormField(formData, `itemList[${index}].unitPrice`, n.unitPrice)
      this.appendFormField(formData, `itemList[${index}].quantity`, n.quantity)
      this.appendFormField(formData, `itemList[${index}].returnId`, n.returnId)
      this.appendFormField(formData, `itemList[${index}].tax`, n.tax)
      this.appendFormField(formData, `itemList[${index}].lineAmount`, n.lineAmount)
      this.appendFormField(formData, `itemList[${index}].itemId`, n.itemId)
      this.appendFormField(formData, `itemList[${index}].isDeleted`, n.isDeleted)
      this.appendFormField(formData, `itemList[${index}].batchId`, n.batchId)
      this.appendFormField(formData, `itemList[${index}].discountAmount`, n.discountAmount)
      this.appendFormField(formData, `itemList[${index}].taxPercent`, n.taxPercent)
      this.appendFormField(formData, `itemList[${index}].id`, n.id)
      this.appendFormField(formData, `itemList[${index}].taxableAmount`, n.taxableAmount)
    })

    formData.append('goodsReturnLinesJson', goodsReturnLinesJson)

    if (p.attachmentFile) {
      formData.append('AttachmentFile', p.attachmentFile, p.attachmentFile.name)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }

    return formData
  }

  private throwIfSaveResultError(data: unknown) {
    if (!data || typeof data !== 'object') return
    const root = data as Record<string, unknown>
    const r = root.result
    if (!r || typeof r !== 'object') return
    const o = r as Record<string, unknown>
    if (o.isError !== true && o.IsError !== true) return
    const msg =
      (typeof o.msg === 'string' && o.msg.trim()) ||
      (typeof o.Msg === 'string' && o.Msg.trim()) ||
      'Operation failed.'
    const err = new Error(msg) as Error & { response?: { status: number; data: unknown } }
    err.response = { status: 400, data }
    throw err
  }

  private async postMultipart(
    url: string,
    label: 'SAVE' | 'UPDATE',
    payload: GoodsReturnSavePayload,
  ): Promise<unknown> {
    const goodsReturnLinesJson = this.buildGoodsReturnLinesJson(payload.itemList)
    const { attachmentFile, itemList, ...master } = payload

    console.log(
      `%c[GoodsReturn] ${label}%c → POST ${url} (multipart/form-data)`,
      'font-weight:bold;color:#fff;background:#0f766e;padding:2px 6px;border-radius:4px',
      'color:inherit',
    )
    console.log('[GoodsReturn] Master (text fields):', master)
    console.log('[GoodsReturn] itemList:', itemList)
    console.log('[GoodsReturn] goodsReturnLinesJson:', goodsReturnLinesJson)
    console.log(
      '[GoodsReturn] AttachmentFile:',
      attachmentFile ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type } : null,
    )

    const formData = this.buildSaveFormData(payload, goodsReturnLinesJson)
    const res = await apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    console.log('%c[GoodsReturn] API response (exact)', 'font-weight:bold;color:#0f766e')
    console.log('status:', res.status)
    console.log('data (object):', res.data)
    try {
      console.log('data (JSON string):', JSON.stringify(res.data))
    } catch {
      console.log('data (JSON string): [not serializable]')
    }

    this.throwIfSaveResultError(res.data)
    return res.data
  }

  /** POST `multipart/form-data` — same fields as `update`; `Id` should be `0` for create. */
  async save(payload: GoodsReturnSavePayload): Promise<unknown> {
    return this.postMultipart(endpoints.goodsReturns.save, 'SAVE', payload)
  }

  /** POST `multipart/form-data` — same body as `save`; `Id` must be the existing goods return id. */
  async update(payload: GoodsReturnSavePayload): Promise<unknown> {
    return this.postMultipart(endpoints.goodsReturns.update, 'UPDATE', payload)
  }

  /** POST `/api/GoodsReturns/Delete/{id}` — path id is the goods return record id. */
  async delete(id: number): Promise<void> {
    await apiClient.post(endpoints.goodsReturns.delete(id))
  }

  private saveResultMessage(data: unknown): string {
    if (!data || typeof data !== 'object') return ''
    const r = (data as Record<string, unknown>).result
    if (!r || typeof r !== 'object') return ''
    const msg = (r as Record<string, unknown>).msg
    return typeof msg === 'string' ? msg.trim() : ''
  }

  /** POST body: JSON array of goods return ids (Swagger: `application/json-patch+json`). */
  async combinedRefund(returnIds: number[]): Promise<string> {
    const ids = returnIds.filter((id) => Number.isFinite(id) && id > 0)
    const res = await apiClient.post(endpoints.goodsReturns.combinedRefund, ids, {
      headers: { 'Content-Type': 'application/json-patch+json' },
    })
    this.throwIfSaveResultError(res.data)
    return this.saveResultMessage(res.data)
  }

  /** POST `/api/GoodsReturns/ConvertToRefund/{id}` — converts one goods return to a vendor credit note (posted). */
  async convertToRefund(id: number): Promise<string> {
    const res = await apiClient.post(endpoints.goodsReturns.convertToRefund(id))
    this.throwIfSaveResultError(res.data)
    return this.saveResultMessage(res.data)
  }
}

export const goodsReturnService = new GoodsReturnService()
